<script>
    import { navigate } from 'svelte-routing';
    import {
        getVerificationCode,
        validateVerificationCode,
        ERROR_CODES,
    } from '../../clients/VerificationCodeClient.svelte';
    import Modal from '../../components/Modal.svelte';
    import { store } from '../../Store.svelte';
    import RadioButton from '../../components/RadioButton.svelte';

    let sendByWhatsApp = true;
    let sendBySMS = false;
    let sendByEmail = false;
    let deliveryMethod = 'WHATSAPP';

    const METHODS = [
        {
            method: 'whatsapp',
            label: `Enviar por WhatsApp: ${$store.personalDetails.numeroCelular}`,
            checked: sendByWhatsApp,
        },
        {
            method: 'sms',
            label: `Enviar por SMS: ${$store.personalDetails.numeroCelular}`,
            checked: sendBySMS,
        },
        {
            method: 'email',
            label: `Enviar por correo electrónico: ${$store.personalDetails.correo}`,
            checked: sendByEmail,
        },
    ];

    let sendButtonText = 'Enviar código';
    let verifyButtonText = 'Siguiente';
    let sendButtonDisabled = false;
    let verifyButtonDisabled = true;
    let isRetrying = false;
    let codeSent = false;

    let errorMessage = '';

    let codeInputs = ['', '', '', ''];
    function codeInput(e, idx) {
        const value = e.target.value;

        if (value.length > 1) {
            const digits = value.split('').slice(0, codeInputs.length);
            codeInputs = [
                ...digits,
                ...Array(codeInputs.length - digits.length).fill(''),
            ];
            e.target.value = value.slice(0, 1);

            const lastFilledIndex = codeInputs.findIndex((val) => !val);
            const focusIndex =
                lastFilledIndex === -1
                    ? codeInputs.length - 1
                    : lastFilledIndex;
            document.getElementById(`code-input-${focusIndex}`).focus();
            return;
        }

        if (value && idx < codeInputs.length - 1) {
            document.getElementById(`code-input-${idx + 1}`).focus();
        }

        if (
            e.inputType === 'deleteContentBackward' &&
            !codeInputs[idx] &&
            idx > 0
        ) {
            document.getElementById(`code-input-${idx - 1}`).focus();
        }
    }

    $: if (codeSent && (sendBySMS || sendByEmail)) {
        setTimeout(() => {
            const firstInput = document.getElementById('code-input-0');
            if (firstInput) firstInput.focus();
        }, 0);
    }

    const sendValidationCode = async () => {
        deliveryMethod = sendByWhatsApp
            ? 'WHATSAPP'
            : sendBySMS
              ? 'SMS'
              : 'CORREO';
        const response = await getVerificationCode($store, deliveryMethod);
        $store.requestedServices.transactionId = response.transactionId;
        return response;
    };

    const sendHandler = async () => {
        sendButtonText = 'Enviando código';
        sendButtonDisabled = true;
        try {
            const response = await sendValidationCode();

            if (response.izziErrorCode === ERROR_CODES.SUCCESS) {
                sendButtonText = 'Código enviado';
                verifyButtonDisabled = false;
                sendButtonDisabled = true;
                codeSent = true;
                errorMessage = '';
            } else {
                MicroModal.show('errorServiceModal');
                sendButtonText = 'Enviar código';
                sendButtonDisabled = false;
            }
        } catch {
            MicroModal.show('errorServiceModal');
            sendButtonText = 'Enviar código';
            sendButtonDisabled = false;
        }
    };

    let retryHandler = async () => {
        isRetrying = true;
        try {
            const response = await sendValidationCode();

            if (response.izziErrorCode === ERROR_CODES.SUCCESS) {
                errorMessage = '';
                verifyButtonDisabled = false;
            } else {
                MicroModal.show('errorServiceModal');
            }
        } catch {
            MicroModal.show('errorServiceModal');
        } finally {
            isRetrying = false;
        }
    };

    function resetSendButton() {
        sendButtonText = 'Enviar código';
        sendButtonDisabled = false;
        verifyButtonDisabled = true;
        codeSent = false;
    }

    function selectMethod(method) {
        sendByWhatsApp = method === 'whatsapp';
        sendBySMS = method === 'sms';
        sendByEmail = method === 'email';
        resetSendButton();
        errorMessage = '';
    }

    let continueWithoutCodeHandler = () => {
        navigate('/compra/direccion', { replace: false });
    };

    let backHandler = () => {
        navigate('/compra/datos', { replace: false });
    };

    let nextHandler = async () => {
        verifyButtonDisabled = true;
        verifyButtonText = 'Verificando...';

        try {
            $store.personalDetails.codigoVerificacion = sendByWhatsApp
                ? 'SI'
                : getVerifUserCode();
            const response = await validateVerificationCode({
                idTransaction: $store.requestedServices.transactionId,
                telefono: $store.personalDetails.numeroCelular,
                correo: $store.personalDetails.correo,
                codigo: $store.personalDetails.codigoVerificacion,
                medio: deliveryMethod,
            });

            codeInputs = ['', '', '', ''];

            if (response.izziErrorCode === ERROR_CODES.SUCCESS) {
                return handleSuccess(response);
            }

            handleError(response);
        } catch (err) {
            MicroModal.show('errorServiceModal');
        } finally {
            verifyButtonText = 'Siguiente';
            verifyButtonDisabled = false;
        }
    };

    function handleSuccess() {
        navigate('/compra/direccion', { replace: false });
    }

    function handleError(response) {
        const izziErrorCode = response.izziErrorCode;
        let modalIdToShow = 'errorServiceModal';

        switch (izziErrorCode) {
            case ERROR_CODES.INVALID_CODE:
                if (sendByWhatsApp) {
                    const nestedResponseCode = response.response?.code;
                    if (nestedResponseCode === ERROR_CODES.DENIED_WHATSAPP) {
                        modalIdToShow = 'warningModal';
                    } else {
                        modalIdToShow = 'noResponseModal';
                    }
                    MicroModal.show(modalIdToShow);
                } else {
                    errorMessage =
                        'Código incorrecto. Por favor, verifica el código enviado.';
                }
                break;
            default:
                modalIdToShow = 'errorServiceModal';
                MicroModal.show(modalIdToShow);
                break;
        }
    }

    $: getVerifUserCode = () => codeInputs.join('');
</script>

<div>
    <div class="pure-g justify-content-center">
        <div class="pure-u-1 pure-u-lg-2-3">
            <div class="pure-g align-items-center m-b-20">
                <div class="pure-u-1 sm-m-b-20">
                    <h1 class="text-fs-26 m-b-0">
                        Verifica tu información de contacto
                    </h1>
                </div>
            </div>
            <div class="panel m-b-25">
                <div
                    class="d-flex flex-flow-column align-items-center m-b-40 gap-2"
                >
                    <div>
                        <p class="m-b-10 fw-semibold">
                            Es necesario que confirmes que deseas realizar la
                            compra.
                        </p>
                        <p class="m-b-20">
                            Selecciona el medio por el cual deseas recibir la
                            <span class="fw-semibold"
                                >aceptación o código de verificación</span
                            >
                        </p>
                        <div
                            class="d-flex flex-flow-column align-items-start m-b-30 gap-1"
                        >
                            {#each METHODS as { method, label, checked }}
                                <RadioButton
                                    id={'sendBy' +
                                        method.charAt(0).toUpperCase() +
                                        method.slice(1)}
                                    name="sendBy"
                                    {label}
                                    color="pink"
                                    {checked}
                                    className="m-b-0 m-t-0"
                                    changeHandler={() => selectMethod(method)}
                                    disabled={codeSent}
                                />
                            {/each}
                        </div>
                        <div
                            class="d-flex flex-flow-column align-items-center gap-2"
                        >
                            <button
                                class="pure__button pure__button--base"
                                on:click={sendHandler}
                                disabled={sendButtonDisabled}
                            >
                                <span class="m-l-5">{sendButtonText}</span>
                            </button>
                        </div>
                    </div>
                    {#if sendByWhatsApp}
                        {#if codeSent}
                            <p class="m-b-10">
                                <span class="fw-semibold"
                                    >Acepta el mensaje</span
                                >
                                que hemos enviado a tu número de celular por WhatsApp.
                            </p>
                        {/if}
                    {:else if sendBySMS || sendByEmail}
                        {#if codeSent}
                            <div
                                class="d-flex flex-flow-column align-items-center"
                            >
                                <p class="m-b-10">
                                    <span class="fw-semibold"
                                        >Introduce el código</span
                                    >
                                    de verificación que hemos enviado a tu número
                                    de celular y correo electrónico.
                                </p>
                                <div
                                    class="code-inputs"
                                    role="group"
                                    aria-labelledby="verification-code-label"
                                >
                                    <span
                                        id="verification-code-label"
                                        class="sr-only"
                                        >Código de verificación</span
                                    >
                                    {#each codeInputs as val, idx}
                                        <input
                                            id={'code-input-' + idx}
                                            bind:value={codeInputs[idx]}
                                            class="rounded-input"
                                            type="number"
                                            maxlength="1"
                                            inputmode="numeric"
                                            on:input={(e) => codeInput(e, idx)}
                                            aria-label={`Dígito ${idx + 1} del código`}
                                            autocomplete="one-time-code"
                                        />
                                    {/each}
                                </div>
                                {#if errorMessage}
                                    <p class="error-text m-t-10 m-b-0">
                                        {errorMessage}
                                    </p>
                                {/if}
                            </div>
                        {/if}
                    {/if}
                    <div class="d-flex flex-flow-column align-items-center">
                        {#if codeSent}
                            <div class="m-b-10">
                                ¿No recibió el código?
                                <button
                                    type="button"
                                    class="pure__button pure__button--link"
                                    on:click={retryHandler}
                                    disabled={isRetrying}
                                >
                                    {isRetrying ? 'Enviando...' : 'Reenviar'}
                                </button>
                            </div>
                            <a
                                href="#/"
                                class="primary-text m-b-10"
                                on:click={() => resetSendButton()}
                                >Seleccionar otro medio de envío</a
                            >
                        {/if}
                        <a
                            href="#/"
                            class="primary-text m-b-10"
                            on:click={continueWithoutCodeHandler}
                            >Continuar sin código</a
                        >
                    </div>
                </div>
                <div class="d-flex align-items-center justify-content-between">
                    <a
                        href="#/"
                        on:click={backHandler}
                        on:keydown={backHandler}
                        class="back-link"
                    >
                        <svg class="icon" aria-hidden="true"
                            ><use
                                xlink:href="/assets/img/icons/symbol-defs.svg#icon-arrow-left"
                            /></svg
                        >
                        <span>atrás</span>
                    </a>
                    <button
                        type="button"
                        on:click={nextHandler}
                        class="pure__button pure__button--base button-hide"
                        disabled={verifyButtonDisabled}
                    >
                        {verifyButtonText}
                    </button>
                </div>
            </div>
        </div>
    </div>
</div>

<Modal
    ident="errorServiceModal"
    title="Error de servicio"
    message="Por favor reintente la verificación, si el problema persiste, por favor contáctenos"
/>
<Modal
    ident="noResponseModal"
    title="Respuesta no recibida"
    message="Respuesta no recibida"
/>
<Modal
    ident="warningModal"
    title="Advertencia"
    message="Advertencia"
    customAction={() => navigate('/', { replace: false })}
/>
